import React, { lazy, Suspense, useContext, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { FeaturesProvider } from '../../contexts/FeaturesContext';
import FeaturesContext from '../../contexts/FeaturesContext';

const PortalCopilot = lazy(() => import('../copilot/PortalCopilot'));

const getStoredUserRole = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')?.role?.toLowerCase();
  } catch {
    return null;
  }
};

/**
 * ClientPortalWrapper - Envuelve las páginas del portal del cliente
 * Aplica los estilos del tema SOLO dentro de este contenedor
 * La landing page y otras páginas públicas NO son afectadas
 */
const ClientPortalContent = ({ children, requiredFeature }) => {
  const { theme } = useTheme();
  const { hasFeature, loading } = useContext(FeaturesContext);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const updateViewport = (event) => setIsMobile(event.matches);
    mediaQuery.addEventListener('change', updateViewport);
    return () => mediaQuery.removeEventListener('change', updateViewport);
  }, []);

  const mobileAllowedRoutes = ['/client', '/client/chats', '/client/calendar', '/client/stages', '/client/applications'];
  if (isMobile && !mobileAllowedRoutes.includes(location.pathname)) {
    return <Navigate to={['admin', 'super_admin'].includes(getStoredUserRole()) ? '/client' : '/client/chats'} replace />;
  }

  // Generar estilos CSS inline basados en el tema
  const portalStyles = {
    '--portal-sidebar-bg': theme.sidebar_bg_color,
    '--portal-sidebar-text': theme.sidebar_text_color,
    '--portal-sidebar-hover': theme.sidebar_hover_color,
    '--portal-sidebar-hover-text': theme.sidebar_hover_text_color,
    '--portal-sidebar-active': theme.sidebar_active_color,
    '--portal-sidebar-active-text': theme.sidebar_active_text_color,
    '--portal-primary-button': theme.primary_button_color,
    '--portal-primary-button-text': theme.primary_button_text_color,
    '--portal-primary-button-hover': theme.primary_button_hover_color,
    '--portal-primary-button-hover-text': theme.primary_button_hover_text_color,
    '--portal-primary-button-gradient-end': theme.primary_button_gradient_end,
    '--portal-secondary-button': theme.secondary_button_color,
    '--portal-secondary-button-text': theme.secondary_button_text_color,
    '--portal-secondary-button-hover': theme.secondary_button_hover_color,
    '--portal-secondary-button-hover-text': theme.secondary_button_hover_text_color,
    '--portal-secondary-button-gradient-end': theme.secondary_button_gradient_end,
    '--portal-button-glow-color': theme.button_glow_color || theme.primary_button_color,
    // Chat bubbles con valores por defecto para evitar burbujas invisibles
    '--portal-chat-incoming': theme.chat_bubble_incoming_color || '#374151',
    '--portal-chat-incoming-text': theme.chat_bubble_incoming_text_color || '#ffffff',
    '--portal-chat-outgoing': theme.chat_bubble_outgoing_color || '#e3fe00',
    '--portal-chat-outgoing-text': theme.chat_bubble_outgoing_text_color || '#000000',
    '--portal-modal-border': theme.modal_border_color,
    '--portal-loading-color': theme.loading_color,
    // Header de modales - usa colores del sidebar
    '--portal-modal-header-bg': theme.modal_header_bg_color || theme.sidebar_bg_color,
    '--portal-modal-header-text': theme.modal_header_text_color || theme.sidebar_text_color,
  };

  // Determinar clases CSS basadas en el tema
  const portalClasses = [
    'client-portal',
    theme.primary_button_gradient ? 'portal-primary-gradient' : '',
    theme.secondary_button_gradient ? 'portal-secondary-gradient' : '',
    theme.button_glow_enabled === true ? 'portal-button-glow' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={portalClasses} style={portalStyles}>
      {!loading && requiredFeature && !hasFeature(requiredFeature) ? <FeatureUnavailable /> : children}
      {!isMobile && !loading && hasFeature('portal_copilot') && <Suspense fallback={null}><PortalCopilot /></Suspense>}
    </div>
  );
};

const FeatureUnavailable = () => <div className="flex min-h-screen items-center justify-center bg-[#f6f7fb] px-5 text-center"><div className="max-w-md rounded-[24px] border border-[#ded9ff] bg-white p-8 shadow-[0_16px_38px_rgba(33,48,78,.08)]"><span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#f1eeff] text-xl text-[#5b43e6]">⌁</span><h1 className="mt-5 text-xl font-semibold text-[#172033]">Función no disponible</h1><p className="mt-2 text-sm leading-6 text-[#758198]">Esta función está desactivada para tu cuenta. Contacta al administrador de la plataforma si necesitas habilitarla.</p></div></div>;

export const ClientPortalWrapper = ({ children, requiredFeature }) => (
  <FeaturesProvider>
    <ClientPortalContent requiredFeature={requiredFeature}>{children}</ClientPortalContent>
  </FeaturesProvider>
);

export default ClientPortalWrapper;
